<template>
  <div class="module-link">
    <label class="module-link__label module-link__label--action">
      {{ $t('Link To') }}

      <NebulaSelect class="module-link__select">
        <template #select>
          <select
            class="nebula-select__element"
            :value="moduleData.options.action"
            @change="changeAction"
          >
            <!-- Images can remain unlinked, but buttons cannot -->
            <NebulaSelectOption
              v-if="moduleData.type === 'asset' || moduleData.type === 'upload'"
              :option="{
                text: $t('None'),
                value: '',
              }"
            />
            <NebulaSelectOption
              :option="{
                text: draft.options.type === 'board'
                  ? $t('Page')
                  : $t('Slide'),
                value: 'page',
              }"
            />
            <NebulaSelectOption
              :option="{
                text: $t('URL'),
                value: 'link',
              }"
            />
            <!-- A modal cannot open another modal -->
            <NebulaSelectOption
              v-if="canvasName !== 'modalModules'"
              :option="{
                text: $t('Window'),
                value: 'window',
              }"
            />
          </select>
        </template>
      </NebulaSelect>
    </label>

    <label
      class="module-link__label module-link__label--target-page"
      v-if="moduleData.options.action === 'page'"
    >
      {{ $t('Destination') }}

      <NebulaSelect class="module-link__select">
        <template #select>
          <select
            class="nebula-select__element"
            :value="moduleData.options.target_page"
            @change="changeTargetPage"
          >
            <optgroup>
              <NebulaSelectOption
                :option="{
                  text: draft.options.type === 'board'
                    ? $t('Previous page')
                    : $t('Previous slide'),
                  value: 'PREV',
                }"
              />
              <NebulaSelectOption
                :option="{
                  text: draft.options.type === 'board'
                    ? $t('Next page')
                    : $t('Next slide'),
                  value: 'NEXT',
                }"
              />
            </optgroup>
            <optgroup>
              <NebulaSelectOption
                v-for="page in formattedPages"
                :key="page.id"
                :option="{
                  isDisabled: page.id === currentPageId,
                  text: page.formattedTitle,
                  value: page.id,
                }"
              />
            </optgroup>
          </select>
        </template>
      </NebulaSelect>
    </label>

    <form
      v-else-if="moduleData.options.action === 'link'"
      class="module-link__url-form"
      @submit.prevent="changeTarget"
    >
      <label class="module-link__label module-link__label--target-link">
        {{ $t('Destination') }}

        <NebulaInput
          ref="urlInput"
          class="module-link__input module-link__input--url"
          placeholder="https://"
          :text="tempUrl"
          type="text"
          @input="tempUrl = $event"
          @blur="tempUrl = $event.target.value"
        />
        <button
          v-if="tempUrl"
          :aria-label="$t('Clear URL')"
          type="button"
          class="module-link__clear-url-button"
          @click="tempUrl = ''"
        >
          <NebulaIcon
            symbol-id="x"
            size="s"
          />
        </button>
      </label>

      <NebulaButton
        v-if="tempUrl === moduleData.options.target"
        class="module-link__main-button"
        :text="$t('Edit')"
        @click="focusUrlInput"
      />
      <NebulaButton
        v-else
        class="module-link__main-button"
        is-submit
        :text="$t('Apply')"
      />
    </form>
    <div v-else-if="moduleData.options.action === 'window'">
      <NebulaButton
        class="module-link__main-button"
        :text="$t('Customize')"
        @click="openModalCanvas"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { cloneDeep } from 'lodash-es';
import {
  NebulaButton,
  NebulaIcon,
  NebulaInput,
  NebulaSelect,
  NebulaSelectOption,
} from '@discoveryedu/nebula-components';
import { formatPageTitle } from '@/lib/utils';
import * as types from '@/lib/constants/store';
import {
  useEditorStore,
  useToastStore,
} from '@/stores';

export default {
  components: {
    NebulaButton,
    NebulaIcon,
    NebulaInput,
    NebulaSelect,
    NebulaSelectOption,
  },
  props: {
    canvasName: {
      type: String,
      required: true,
    },
    moduleData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      tempUrl: this.moduleData.options.target,
    };
  },
  computed: {
    ...mapState(useEditorStore, [
      'currentPageId',
      'draft',
      'pagesSorted',
      'modalCanvases',
    ]),
    formattedPages() {
      return this.pagesSorted.map((page, index) => ({
        ...page,
        formattedTitle: formatPageTitle(page, index, this.draft.options.type),
      }));
    },
  },
  beforeUnmount() {
    // If the link url has been updated, save it before destroying
    if (this.moduleData.options.action === 'link'
      && this.tempUrl !== this.moduleData.options.target) {
      this.changeTarget();
    }
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.ADD_MODAL_CANVAS,
      types.UPDATE_CURRENTLY_EDITING,
      types.UPDATE_MODULE,
      types.VIEW_MODAL_CANVAS_BY_ID,
    ]),
    ...mapActions(useToastStore, [
      types.SET_STUDIO_TOAST,
    ]),
    update(data) {
      const payload = cloneDeep(data);

      if (payload.options?.target && payload.options?.action === 'link') {
        // use a cleaned up url or empty string
        payload.options.target = this.getValidatedUrl(payload.options.target) || '';

        if (!payload.options.target) {
          this[types.SET_STUDIO_TOAST]({ type: 'error', message: 'Invalid Link Url.' });
        }
      }
      this[types.UPDATE_MODULE]({ canvasName: this.canvasName, payload });
    },
    changeAction(event) {
      const data = cloneDeep(this.moduleData);
      data.options.action = event.target.value || undefined;

      // Clear or update the target page since it means something different
      // depending on the action
      if (data.options.action === 'page') {
        data.options.target_page = undefined;
      } else if (data.options.action === 'window') {
        // For now, each module can only link to its own modal. After we are able
        // to choose a specific modal, this can be cleared out instead
        data.options.target_page = this.moduleData.id;
      }

      this.update(data);
    },
    changeTarget() {
      const data = cloneDeep(this.moduleData);
      data.options.target = this.tempUrl;
      this.update(data);
    },
    changeTargetPage(event) {
      const data = cloneDeep(this.moduleData);
      data.options.target_page = event.target.value;
      this.update(data);
    },
    focusUrlInput() {
      // The Edit button actually does nothing
      this.$refs.urlInput.$el.focus();
    },
    getValidatedUrl(url) {
      try {
        // use URL constructor to validate the url string
        const tmp = new URL(url);

        // validate fully qualified url
        if (!['http:', 'https:'].includes(tmp.protocol) || !tmp.origin) {
          return false;
        }

        // ensure an https protocol
        tmp.protocol = 'https:';

        // return the cleaned up url
        return tmp.toString();
      } catch {
        // we don't accept invalid urls
        return false;
      }
    },
    openModalCanvas() {
      // Close this mini-menu
      this[types.UPDATE_CURRENTLY_EDITING]({
        [this.moduleData.id]: null,
      });

      const existingModal = this.modalCanvases
        .find((modal) => modal.id === this.moduleData.options.target_page);

      // If the modal doesn't already exist, create it
      if (!existingModal) {
        this[types.ADD_MODAL_CANVAS]({ id: this.moduleData.options.target_page });
      }

      // Open the modal canvas
      this[types.VIEW_MODAL_CANVAS_BY_ID](this.moduleData.options.target_page);
    },
  },
  watch: {
    moduleData: {
      handler(newModuleData) {
        this.tempUrl = newModuleData.options.target;
      },
      deep: true,
    },
  },
};
</script>

<style lang="stylus">
.module-link {
  align-items: flex-end;
  display: inline-flex;

  &__label {
    flex: 1 1 50%;
    font-size: $nebula-font-size-body-2;
    font-weight: 500; // matching other labels in module options
    padding: 0 $nebula-space-1x;

    &--target-link {
      position: relative;
    }
  }

  &__input,
  &__select {
    margin-bottom: 0;
    margin-top: $nebula-space-1x;
    min-width: 150px;
  }

  &__input {
    width: 224px;
  }

  &__input--url {
    padding-inline-end: $nebula-space-3x;
  }

  &__main-button {
    margin: 0 $nebula-space-half;
  }

  &__url-form {
    align-items: flex-end;
    display: flex;
  }

  &__clear-url-button {
    background: none;
    border: 0;
    border-radius: 0;
    bottom: 5px; // center vertically inside input
    margin: 0;
    padding: 6px;
    position: absolute;
    inset-inline-end: $nebula-space-1x;

    svg {
      display: block;
      fill: $comet-color-interactive-default;
    }
  }
}
</style>
